// WHAT: Custom hook for creating roadmap items via Tauri command
// WHY:  Provides a reusable function for creating roadmap items with error handling
// DOMAIN: Admin → Roadmap
// DEPENDENCIES: Tauri core API, Roadmap_Item type
// RISKS:
//   - NOTE: No validation of item data before insertion
//   - NOTE: Error is converted to string - may lose error details

import { core } from "@tauri-apps/api";

import { type Roadmap_Item } from "../types/roadmap-types";

/**
 * WHAT:
 *   Custom hook that returns a function to create roadmap items.
 *
 * WHY:
 *   Encapsulates roadmap item creation logic using Tauri commands instead of
 *   direct Supabase access for better security and error handling.
 *
 * RETURNS:
 *   (item: Roadmap_Item) => Promise<{ success: boolean; error?: string }>
 *   — Async function to create roadmap item
 *
 * @param item - Roadmap item to create
 *
 * @returns {(item: Roadmap_Item) => Promise<{ success: boolean; error?: string }>} Async-Funktion zum Erstellen von Roadmap-Items
 *
 * BEHAVIOR:
 *   - Invokes Tauri command to insert roadmap item
 *   - Returns success status and optional error message
 *   - Handles errors and converts to string
 *
 * RISKS:
 *   - NOTE: No validation of item data - invalid data may cause backend errors
 *   - NOTE: Error conversion to string may lose error type information
 *   - NOTE: Success doesn't guarantee item was created - check return value
 *
 */
const createRoadmapItem = async (
  item: Roadmap_Item,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // NOTE: Verwende Tauri Command statt Supabase direkt - better security
    await core.invoke("insert_roadmap_item", { item });

    return { success: true };
  } catch (error) {
    return { error: String(error), success: false };
  }
};

export const useRoadmapCreate = () => {
  return createRoadmapItem;
};
